import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  FiActivity,
  FiPlus,
  FiSearch,
  FiPhone,
  FiMail,
  FiMapPin,
  FiTrash2,
  FiEdit,
  FiEye,
  FiMessageSquare,
  FiCheckCircle,
  FiAlertCircle,
  FiClock,
  FiX,
  FiCheck,
  FiRefreshCw,
} from "react-icons/fi";
import {
  getHealthiansLeads,
  addHealthiansLead,
  editHealthiansLead,
  deleteHealthiansLead,
  getHealthiansLeadNotes,
  addHealthiansLeadNote,
  deleteHealthiansLeadNote,
} from "../../../api/customer/customerFeatureApis";

const HealthiansHub = () => {
  const authState = useSelector((state) => state.auth);
  const loggedInUser = authState?.user ?? authState;
  const token =
    loggedInUser?.refreshToken ||
    authState?.refreshToken ||
    loggedInUser?.token ||
    authState?.token;

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Add/Edit modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    mobile_number: "",
    email: "",
    city: "",
    state: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [submittingLead, setSubmittingLead] = useState(false);
  const [modalError, setModalError] = useState(null);

  // Notes modal state
  const [isNotesOpen, setIsNotesOpen] = useState(false);
  const [selectedLeadForNotes, setSelectedLeadForNotes] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [newNoteText, setNewNoteText] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [notesError, setNotesError] = useState(null);

  const [toastMessage, setToastMessage] = useState(null);

  const showToast = (msg) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3500);
  };

  const loadLeads = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);

    try {
      const res = await getHealthiansLeads(token, {
        page_number: 1,
        page_size: 50,
        order_by: "DESC",
        keyword_search: searchQuery,
      });
      const list = res?.serviceLeads || res?.leads || [];
      setLeads(list);
    } catch (err) {
      setError("Unable to load health service leads. Please check your network.");
    } finally {
      setLoading(false);
    }
  }, [token, searchQuery]);

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  const handleOpenAdd = () => {
    setEditingLead(null);
    setFormData({
      first_name: "",
      last_name: "",
      mobile_number: "",
      email: "",
      city: "",
      state: "",
    });
    setFormErrors({});
    setModalError(null);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (lead, e) => {
    e?.stopPropagation();
    setEditingLead(lead);
    setFormData({
      first_name: lead.first_name || "",
      last_name: lead.last_name || "",
      mobile_number: lead.mobile_number || "",
      email: lead.email || "",
      city: lead.city || "",
      state: lead.state || "",
    });
    setFormErrors({});
    setModalError(null);
    setIsModalOpen(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.first_name.trim()) errors.first_name = "First name is required";
    if (!formData.mobile_number.trim()) {
      errors.mobile_number = "Mobile number is required";
    } else if (!/^[0-9]{10}$/.test(formData.mobile_number.trim())) {
      errors.mobile_number = "Enter a valid 10-digit mobile number";
    }
    if (!formData.state.trim()) errors.state = "State is required";
    if (!formData.city.trim()) errors.city = "City is required";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmittingLead(true);
    setModalError(null);

    const payload = {
      ...(editingLead && { id: editingLead.id }),
      first_name: formData.first_name.trim(),
      last_name: formData.last_name.trim(),
      mobile_number: formData.mobile_number.trim(),
      email: formData.email.trim(),
      city: formData.city.trim(),
      state: formData.state.trim(),
      associated_partner: { id: 1 },
      status: editingLead?.status || "PENDING",
    };

    try {
      const res = editingLead
        ? await editHealthiansLead(token, payload)
        : await addHealthiansLead(token, payload);

      if (res?.statusType === "SUCCESS" || res?.serviceLead) {
        showToast(editingLead ? "Service lead updated!" : "Service lead submitted successfully!");
        setIsModalOpen(false);
        loadLeads();
      } else {
        setModalError(res?.text || "Failed to save health service lead.");
      }
    } catch (err) {
      setModalError(err?.response?.data?.text || "An error occurred.");
    } finally {
      setSubmittingLead(false);
    }
  };

  const handleDeleteLead = async (lead, e) => {
    e?.stopPropagation();
    if (!window.confirm(`Are you sure you want to delete lead for ${lead.first_name} ${lead.last_name}?`)) return;

    try {
      const res = await deleteHealthiansLead(token, lead.id);
      if (res?.statusType === "SUCCESS" || res?.text) {
        showToast("Lead successfully deleted.");
        setLeads((prev) => prev.filter((l) => l.id !== lead.id));
      } else {
        alert(res?.text || "Failed to delete lead.");
      }
    } catch (err) {
      alert("Failed to delete service lead.");
    }
  };

  // Notes functions
  const handleOpenNotes = async (lead, e) => {
    e?.stopPropagation();
    setSelectedLeadForNotes(lead);
    setIsNotesOpen(true);
    setNewNoteText("");
    setNotesError(null);
    setLoadingNotes(true);

    try {
      const res = await getHealthiansLeadNotes(token, lead.id);
      setNotes(res?.notes || res?.serviceNotes || []);
    } catch (err) {
      setNotesError("Failed to load notes for this service lead.");
    } finally {
      setLoadingNotes(false);
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNoteText.trim() || !selectedLeadForNotes) return;

    setAddingNote(true);
    setNotesError(null);

    try {
      const payload = {
        lead_id: selectedLeadForNotes.id,
        note: newNoteText.trim(),
      };
      const res = await addHealthiansLeadNote(token, payload);
      if (res?.statusType === "SUCCESS" || res?.note) {
        setNewNoteText("");
        const refreshed = await getHealthiansLeadNotes(token, selectedLeadForNotes.id);
        setNotes(refreshed?.notes || refreshed?.serviceNotes || []);
      } else {
        setNotesError(res?.text || "Failed to add note.");
      }
    } catch (err) {
      setNotesError("Error saving note.");
    } finally {
      setAddingNote(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await deleteHealthiansLeadNote(token, noteId);
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
    } catch (err) {
      setNotesError("Failed to delete note.");
    }
  };

  const getStatusBadge = (status) => {
    const s = (status || "").toUpperCase();
    if (s === "NEW") {
      return <span className="badge bg-success-subtle text-success px-2 py-1">New</span>;
    }
    if (s === "COMPLETED") {
      return <span className="badge bg-primary-subtle text-primary px-2 py-1">Completed</span>;
    }
    return <span className="badge bg-warning-subtle text-warning px-2 py-1">Pending</span>;
  };

  return (
    <div className="d-flex flex-column gap-4 w-100">
      {/* Toast Notification */}
      {toastMessage && (
        <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 1100 }}>
          <div className="toast show align-items-center text-bg-success border-0 shadow">
            <div className="d-flex">
              <div className="toast-body d-flex align-items-center gap-2">
                <FiCheckCircle size={18} />
                <span>{toastMessage}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Banner */}
      <div
        className="rounded-4 p-4 p-md-5 text-white shadow-sm position-relative overflow-hidden"
        style={{
          background: "linear-gradient(135deg, #047857 0%, #064e3b 100%)",
        }}
      >
        <div className="position-relative" style={{ zIndex: 1, maxWidth: "600px" }}>
          <div className="d-inline-flex align-items-center gap-2 px-3 py-1 rounded-pill bg-white/10 text-emerald-200 mb-3 border border-emerald-400">
            <FiActivity size={16} />
            <span className="small fw-bold">Healthcare & Diagnostics</span>
          </div>

          <h1 className="fw-bold mb-2 fs-2 text-white">Healthians Diagnostic Services</h1>
          <p className="text-light opacity-90 small mb-4 lh-base">
            Book verified preventive health checkups, blood tests, and doctor consultations with free home sample
            collection. Track your leads and service requests in real time.
          </p>

          <button
            type="button"
            className="btn btn-light fw-bold px-4 py-2 d-inline-flex align-items-center gap-2 text-emerald-900 shadow-sm"
            onClick={handleOpenAdd}
          >
            <FiPlus size={18} />
            <span>Book New Diagnostic Lead</span>
          </button>
        </div>
      </div>

      {/* Controls */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
        <div className="position-relative flex-grow-1" style={{ maxWidth: "400px" }}>
          <FiSearch size={16} className="position-absolute top-50 translate-middle-y text-muted" style={{ left: "14px" }} />
          <input
            type="text"
            className="form-control ps-5 py-2 shadow-sm border-slate-200"
            placeholder="Search leads by patient name, phone, or location..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <button
          type="button"
          className="btn btn-outline-secondary d-inline-flex align-items-center gap-1 shadow-sm"
          onClick={loadLeads}
          disabled={loading}
        >
          <FiRefreshCw size={14} className={loading ? "spin" : ""} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-success" role="status"></div>
          <p className="text-muted small mt-2">Loading health service leads...</p>
        </div>
      ) : error ? (
        <div className="alert alert-danger d-flex align-items-center justify-content-between p-3" role="alert">
          <span>{error}</span>
          <button type="button" className="btn btn-sm btn-outline-danger" onClick={loadLeads}>
            Retry
          </button>
        </div>
      ) : leads.length === 0 ? (
        <div className="text-center py-5 bg-white rounded-3 border border-slate-200 shadow-sm p-4">
          <div
            className="d-inline-flex align-items-center justify-content-center bg-emerald-50 text-success rounded-circle mb-3"
            style={{ width: "56px", height: "56px" }}
          >
            <FiActivity size={28} />
          </div>
          <h3 className="fs-5 fw-bold text-dark mb-1">No Diagnostic Health Leads Found</h3>
          <p className="text-muted small max-w-md mx-auto mb-3">
            Schedule a lab test or full body checkup for yourself or family members with free home collection.
          </p>
          <button type="button" className="btn btn-success btn-sm px-4" onClick={handleOpenAdd}>
            Schedule Lab Test
          </button>
        </div>
      ) : (
        <div className="row g-3">
          {leads.map((item) => {
            const fullName = [item.first_name, item.last_name].filter(Boolean).join(" ");
            const locationText = [item.city, item.state].filter(Boolean).join(", ");
            const dateStr = item.created_on
              ? new Date(item.created_on).toLocaleDateString("en-IN", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })
              : null;

            return (
              <div key={item.id} className="col-12 col-md-6 col-lg-4">
                <div className="p-4 bg-white rounded-3 border border-slate-200 shadow-sm h-100 d-flex flex-column justify-content-between">
                  <div>
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <span className="fw-bold text-dark fs-6">{fullName || "Patient Lead"}</span>
                      {getStatusBadge(item.status)}
                    </div>

                    <div className="d-flex flex-column gap-1 text-secondary small mb-3">
                      {item.mobile_number && (
                        <div className="d-flex align-items-center gap-2">
                          <FiPhone size={13} className="text-muted" />
                          <span>{item.mobile_number}</span>
                        </div>
                      )}
                      {item.email && (
                        <div className="d-flex align-items-center gap-2">
                          <FiMail size={13} className="text-muted" />
                          <span>{item.email}</span>
                        </div>
                      )}
                      {locationText && (
                        <div className="d-flex align-items-center gap-2">
                          <FiMapPin size={13} className="text-muted" />
                          <span>{locationText}</span>
                        </div>
                      )}
                      {dateStr && (
                        <div className="d-flex align-items-center gap-2 text-muted">
                          <FiClock size={13} />
                          <span>Requested on {dateStr}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="pt-3 border-top border-slate-100 d-flex align-items-center justify-content-between">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1 py-1"
                      onClick={(e) => handleOpenNotes(item, e)}
                    >
                      <FiMessageSquare size={14} />
                      <span>Updates & Notes</span>
                    </button>

                    <div className="d-flex align-items-center gap-1">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary py-1 px-2"
                        onClick={(e) => handleOpenEdit(item, e)}
                        title="Edit lead"
                      >
                        <FiEdit size={14} />
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger py-1 px-2"
                        onClick={(e) => handleDeleteLead(item, e)}
                        title="Delete lead"
                      >
                        <FiTrash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add / Edit Lead Modal */}
      {isModalOpen && (
        <div className="customer-modal-overlay" role="dialog" aria-modal="true">
          <div className="customer-modal" style={{ maxWidth: "540px" }}>
            <div className="customer-modal__header">
              <h2 className="customer-modal__title">
                {editingLead ? "Edit Health Service Lead" : "Schedule Diagnostic Lead"}
              </h2>
              <button
                type="button"
                className="btn btn-sm btn-light border-0"
                onClick={() => setIsModalOpen(false)}
                aria-label="Close dialog"
              >
                <FiX size={20} />
              </button>
            </div>

            <form onSubmit={handleFormSubmit}>
              <div className="customer-modal__body">
                {modalError && <div className="alert alert-danger py-2 small mb-0">{modalError}</div>}

                <div className="row g-3">
                  <div className="col-12 col-md-6">
                    <div className="customer-form-group">
                      <label className="customer-form-label">
                        First Name <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        name="first_name"
                        className="customer-form-input"
                        placeholder="e.g. Rahul"
                        value={formData.first_name}
                        onChange={handleFormChange}
                      />
                      {formErrors.first_name && (
                        <span className="customer-form-error">{formErrors.first_name}</span>
                      )}
                    </div>
                  </div>

                  <div className="col-12 col-md-6">
                    <div className="customer-form-group">
                      <label className="customer-form-label">Last Name</label>
                      <input
                        type="text"
                        name="last_name"
                        className="customer-form-input"
                        placeholder="e.g. Sharma"
                        value={formData.last_name}
                        onChange={handleFormChange}
                      />
                    </div>
                  </div>
                </div>

                <div className="row g-3">
                  <div className="col-12 col-md-6">
                    <div className="customer-form-group">
                      <label className="customer-form-label">
                        Mobile Number <span className="required">*</span>
                      </label>
                      <input
                        type="tel"
                        name="mobile_number"
                        maxLength="10"
                        className="customer-form-input"
                        placeholder="10-digit number"
                        value={formData.mobile_number}
                        onChange={handleFormChange}
                      />
                      {formErrors.mobile_number && (
                        <span className="customer-form-error">{formErrors.mobile_number}</span>
                      )}
                    </div>
                  </div>

                  <div className="col-12 col-md-6">
                    <div className="customer-form-group">
                      <label className="customer-form-label">Email Address</label>
                      <input
                        type="email"
                        name="email"
                        className="customer-form-input"
                        placeholder="patient@example.com"
                        value={formData.email}
                        onChange={handleFormChange}
                      />
                    </div>
                  </div>
                </div>

                <div className="row g-3">
                  <div className="col-12 col-md-6">
                    <div className="customer-form-group">
                      <label className="customer-form-label">
                        State <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        name="state"
                        className="customer-form-input"
                        placeholder="e.g. Delhi"
                        value={formData.state}
                        onChange={handleFormChange}
                      />
                      {formErrors.state && (
                        <span className="customer-form-error">{formErrors.state}</span>
                      )}
                    </div>
                  </div>

                  <div className="col-12 col-md-6">
                    <div className="customer-form-group">
                      <label className="customer-form-label">
                        City <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        name="city"
                        className="customer-form-input"
                        placeholder="e.g. New Delhi"
                        value={formData.city}
                        onChange={handleFormChange}
                      />
                      {formErrors.city && (
                        <span className="customer-form-error">{formErrors.city}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="customer-modal__footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => setIsModalOpen(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-success btn-sm px-4" disabled={submittingLead}>
                  {submittingLead ? "Submitting..." : editingLead ? "Update Lead" : "Confirm Lab Lead"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Notes Modal */}
      {isNotesOpen && selectedLeadForNotes && (
        <div className="customer-modal-overlay" role="dialog" aria-modal="true">
          <div className="customer-modal" style={{ maxWidth: "580px" }}>
            <div className="customer-modal__header">
              <div>
                <h2 className="customer-modal__title">
                  Notes for {selectedLeadForNotes.first_name} {selectedLeadForNotes.last_name}
                </h2>
                <div className="text-muted small mt-1">
                  {selectedLeadForNotes.mobile_number} • {selectedLeadForNotes.city}
                </div>
              </div>
              <button
                type="button"
                className="btn btn-sm btn-light border-0"
                onClick={() => setIsNotesOpen(false)}
              >
                <FiX size={20} />
              </button>
            </div>

            <div className="customer-modal__body">
              {notesError && <div className="alert alert-danger py-2 small mb-0">{notesError}</div>}

              {/* Add Note Form */}
              <form onSubmit={handleAddNote} className="d-flex gap-2">
                <input
                  type="text"
                  className="customer-form-input"
                  placeholder="Record interaction note or sample collection update..."
                  value={newNoteText}
                  onChange={(e) => setNewNoteText(e.target.value)}
                  disabled={addingNote}
                />
                <button
                  type="submit"
                  className="btn btn-success btn-sm px-3 flex-shrink-0"
                  disabled={addingNote || !newNoteText.trim()}
                >
                  {addingNote ? "Adding..." : "Add Note"}
                </button>
              </form>

              {loadingNotes ? (
                <div className="text-center py-4">
                  <div className="spinner-border spinner-border-sm text-success me-2" role="status"></div>
                  <span className="text-muted small">Loading notes...</span>
                </div>
              ) : notes.length === 0 ? (
                <div className="text-center py-4 bg-light rounded-2 text-muted small">
                  No notes recorded yet for this health service lead.
                </div>
              ) : (
                <div className="d-flex flex-column gap-2 mt-2">
                  {notes.map((n) => (
                    <div key={n.id} className="p-3 bg-light rounded-2 border d-flex justify-content-between gap-3">
                      <div className="d-flex flex-column gap-1">
                        <span className="text-dark small" style={{ whiteSpace: "pre-wrap" }}>
                          {n.note}
                        </span>
                        {n.created_on && (
                          <span className="text-muted" style={{ fontSize: "0.75rem" }}>
                            {new Date(n.created_on).toLocaleString("en-IN")}
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm btn-link text-danger p-0"
                        onClick={() => handleDeleteNote(n.id)}
                        title="Delete note"
                      >
                        <FiTrash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="customer-modal__footer">
              <button
                type="button"
                className="btn btn-secondary btn-sm px-4"
                onClick={() => setIsNotesOpen(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HealthiansHub;
